/**
 * Config Parser
 * 
 * Converts BacktestRequest JSON from backend to UI state format.
 * This is the Backend -> UI direction of the bidirectional config system.
 * The counterpart (configGenerator) handles UI -> Backend direction.
 */

import { Leg, OrGroups as UIOrGroups } from "../types/flowchart";
import { ConfiguredSignal, ConfiguredDynamicCondition, generateId } from "../../../../config/signals";
import { TradeCondition as UITradeCondition } from "../flowchart/TradeConditions";
import {
  BacktestRequest,
  BackendOrGroups,
  SignalSpec,
  BackendTradeCondition,
  BackendDynamicCondition,
} from "./types";
import { UIBacktestState } from "./configGenerator";
import logger from '../../../../logging/logger';

// Known invalid keys that are not signals (e.g., trade condition types)
const INVALID_SIGNAL_TYPES = new Set(["Option", "Stock", "Future"]);

function toUiLegNumber(value: unknown): unknown {
  if (value === undefined || value === null || value === "") return value;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value + 1;
  }

  if (typeof value === "string") {
    if (value.includes(",")) {
      return value
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part !== "")
        .map((part) => Number(part))
        .filter((num) => Number.isFinite(num))
        .map((num) => String(num + 1))
        .join(",");
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? String(parsed + 1) : value;
  }

  if (Array.isArray(value)) {
    return value
      .map((part) => Number(part))
      .filter((num) => Number.isFinite(num))
      .map((num) => num + 1);
  }

  return value;
}

function normalizeUiParams(
  params: Record<string, unknown>,
  allowMultipleLegIdx = false
): Record<string, unknown> {
  if (!Object.prototype.hasOwnProperty.call(params, "leg_idx")) {
    return params;
  }

  const convertedLegRef = toUiLegNumber(params.leg_idx);
  const normalizedLegRef = allowMultipleLegIdx
    ? convertedLegRef
    : Array.isArray(convertedLegRef)
      ? convertedLegRef[0]
      : (typeof convertedLegRef === "string" && convertedLegRef.includes(","))
        ? convertedLegRef.split(",")[0]?.trim()
        : convertedLegRef;

  return {
    ...params,
    leg_idx: normalizedLegRef,
  };
}

function normalizeIntradayMaUiParams(
  signalType: string,
  params: Record<string, unknown>
): Record<string, unknown> {
  if (signalType !== "intraday_ma_crossover") {
    return params;
  }

  const frequency = params.frequency;
  if (!frequency || typeof frequency !== "object" || Array.isArray(frequency)) {
    return params;
  }

  const unit = (frequency as { unit?: unknown }).unit;
  if (unit !== "seconds" && unit !== "minutes" && unit !== "hours") {
    return params;
  }

  return {
    ...params,
    frequency: {
      every: 1,
      unit,
    },
  };
}

/**
 * Convert a backend SignalSpec to UI ConfiguredSignal
 *
 * Note: Percentage parameters are stored as decimals (0.03 = 3%) in both backend and UI params.
 * The SignalParamPopover component handles the display conversion (multiplying by 100 for display).
 */
function parseSignal(signalSpec: SignalSpec): ConfiguredSignal | null {
  const signalTypes = Object.keys(signalSpec);
  if (signalTypes.length === 0) return null;

  const type = signalTypes[0];

  // Skip invalid signal types (e.g., trade condition types that were incorrectly parsed)
  if (INVALID_SIGNAL_TYPES.has(type)) {
    logger.warn(`Skipping invalid signal type: ${type}`);
    return null;
  }

  const normalized = normalizeUiParams(signalSpec[type] || {}, false);
  const params = normalizeIntradayMaUiParams(type, normalized);

  return {
    id: generateId(),
    type,
    params: { ...params },
  };
}

/**
 * Convert backend OrGroups to UI OrGroups (ConfiguredSignal[][])
 */
function parseOrGroups(backendOrGroups: BackendOrGroups | undefined): UIOrGroups {
  if (!backendOrGroups || backendOrGroups.length === 0) {
    // Return empty array - UI will show "Add" button
    return [];
  }

  const result: UIOrGroups = [];

  for (const andGroup of backendOrGroups) {
    const uiAndGroup: ConfiguredSignal[] = [];

    for (const signalSpec of andGroup) {
      const signal = parseSignal(signalSpec);
      if (signal) {
        uiAndGroup.push(signal);
      }
    }

    if (uiAndGroup.length > 0) {
      result.push(uiAndGroup);
    }
  }

  return result;
}

/**
 * Convert backend dynamic conditions to UI format
 */
function parseDynamicConditions(
  backendConditions: BackendDynamicCondition | undefined
): ConfiguredDynamicCondition[] {
  if (!backendConditions || backendConditions.length === 0) {
    return [];
  }

  return backendConditions.map((condSpec) => {
    const types = Object.keys(condSpec);
    const type = types[0] || "";
    const params = normalizeUiParams(condSpec[type] || {}, type === "delta_hedge_quantity");

    return {
      id: generateId(),
      type,
      params: { ...params },
    };
  });
}

type GreekType = "delta" | "gamma" | "theta" | "vega" | "rho" | "strike" | "iv" | "Dynamic" | "";

interface GreekCondition {
  id: string;
  greek: GreekType;
  greekValue?: number;
  dynamicGreek?: string;
}

/**
 * Build greekConditions array from individual greek fields
 */
function buildGreekConditions(opt: {
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  rho?: number;
  strike?: number;
  implied_vol?: number;
}): GreekCondition[] {
  const greekConditions: GreekCondition[] = [];

  if (opt.delta !== undefined && opt.delta !== null) {
    greekConditions.push({ id: generateId(), greek: "delta", greekValue: opt.delta });
  }
  if (opt.gamma !== undefined && opt.gamma !== null) {
    greekConditions.push({ id: generateId(), greek: "gamma", greekValue: opt.gamma });
  }
  if (opt.theta !== undefined && opt.theta !== null) {
    greekConditions.push({ id: generateId(), greek: "theta", greekValue: opt.theta });
  }
  if (opt.vega !== undefined && opt.vega !== null) {
    greekConditions.push({ id: generateId(), greek: "vega", greekValue: opt.vega });
  }
  if (opt.rho !== undefined && opt.rho !== null) {
    greekConditions.push({ id: generateId(), greek: "rho", greekValue: opt.rho });
  }
  if (opt.strike !== undefined && opt.strike !== null) {
    greekConditions.push({ id: generateId(), greek: "strike", greekValue: opt.strike });
  }
  if (opt.implied_vol !== undefined && opt.implied_vol !== null) {
    greekConditions.push({ id: generateId(), greek: "iv", greekValue: opt.implied_vol });
  }

  return greekConditions;
}

/**
 * Convert backend TradeCondition to UI TradeCondition
 */
function parseTradeCondition(
  backendCondition: BackendTradeCondition
): UITradeCondition {
  if ("Option" in backendCondition) {
    const opt = backendCondition.Option;
    const greekConditions = buildGreekConditions(opt);

    // Determine expirationMode based on which field is present
    // DTE takes precedence if both are somehow present
    let expirationMode: "expiration" | "dte" | undefined;
    if (opt.dte !== undefined && opt.dte !== null) {
      expirationMode = "dte";
    } else if (opt.expiration !== undefined && opt.expiration !== null && opt.expiration !== "") {
      expirationMode = "expiration";
    }

    return {
      id: generateId(),
      instrumentType: "Option",
      symbol: opt.symbol,
      optionType: opt.instrument,
      strike: opt.strike,
      expirationMode,
      expiration: opt.expiration,
      dte: opt.dte,
      impliedVol: opt.implied_vol,
      delta: opt.delta,
      gamma: opt.gamma,
      theta: opt.theta,
      vega: opt.vega,
      rho: opt.rho,
      quantity: opt.quantity,
      direction: opt.direction === "Long" ? "long" : "short",
      dynamicEnabled: !!opt.dynamic && opt.dynamic.length > 0,
      dynamicConditions: parseDynamicConditions(opt.dynamic),
      greekConditions: greekConditions.length > 0 ? greekConditions : undefined,
    };
  } else if ("Stock" in backendCondition) {
    const stock = backendCondition.Stock;
    return {
      id: generateId(),
      instrumentType: "Stock",
      symbol: stock.symbol,
      quantity: stock.quantity,
      direction: stock.direction === "Long" ? "long" : "short",
      dynamicEnabled: !!stock.dynamic && stock.dynamic.length > 0,
      dynamicConditions: parseDynamicConditions(stock.dynamic),
    };
  } else if ("Future" in backendCondition) {
    const future = backendCondition.Future;
    return {
      id: generateId(),
      instrumentType: "Stock", // Future mapped to Stock in UI
      symbol: future.symbol,
      quantity: future.quantity,
      direction: future.direction === "Long" ? "long" : "short",
      dynamicEnabled: !!future.dynamic && future.dynamic.length > 0,
      dynamicConditions: parseDynamicConditions(future.dynamic),
    };
  }

  // Fallback
  return {
    id: generateId(),
    instrumentType: "Stock",
    direction: "long",
  };
}

/**
 * Normalize symbol list for UI usage
 */
function normalizeSymbols(rawSymbols: Array<string | undefined | null> | undefined): string[] {
  if (!rawSymbols || rawSymbols.length === 0) return [];

  const symbols = new Set<string>();
  for (const symbol of rawSymbols) {
    const normalized = (symbol || "").trim().toUpperCase();
    if (!normalized) continue;
    symbols.add(normalized);
  }

  return Array.from(symbols);
}

/**
 * Extract symbols from strategy_info
 */
function extractLegSymbols(strategyInfo: Record<string, BackendTradeCondition>): string[] {
  const symbols: Array<string | undefined | null> = [];
  for (const condition of Object.values(strategyInfo)) {
    if ("Option" in condition) {
      symbols.push(condition.Option.symbol);
    } else if ("Stock" in condition) {
      symbols.push(condition.Stock.symbol);
    } else if ("Future" in condition) {
      symbols.push(condition.Future.symbol);
    }
  }
  return normalizeSymbols(symbols);
}

/**
 * Main parser: Convert BacktestRequest to UIBacktestState
 */
export function parseBacktestConfig(config: BacktestRequest): UIBacktestState {

  const legKeys = Object.keys(config.strategy_info).sort((a, b) => Number(a) - Number(b));

  const legs: Leg[] = legKeys.map((legKey, idx) => {
    const legSignals = config.signal_map?.[legKey];
    const entryRules = parseOrGroups(legSignals?.entry_signals);
    const exitRules = parseOrGroups(legSignals?.exit_signals);

    return {
      id: generateId(),
      name: `Leg ${idx + 1}`,
      entryRules,
      exitRules,
      tradeConditions: [parseTradeCondition(config.strategy_info[legKey])],
    };
  });

  const hasStrategySignals = config.entry_signals.length > 0 || config.exit_signals.length > 0;
  const strategyEntryRules = parseOrGroups(config.entry_signals);
  const strategyExitRules = parseOrGroups(config.exit_signals);
  const strategySymbols = normalizeSymbols(config.symbols);
  const symbolBank = strategySymbols.length > 0
    ? strategySymbols
    : extractLegSymbols(config.strategy_info);

  return {
    legs: legs.length > 0 ? legs : [],
    symbolBank,
    strategyName: "AI Generated Strategy",
    startDate: config.start_date || "",
    endDate: config.end_date || "",
    strategySignalsEnabled: hasStrategySignals,
    strategyEntryRules,
    strategyExitRules,
  };
}
